import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  LayoutDashboard, Box, User, RefreshCw, Download,
  FileText, MessageSquare, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

// Import refactored admin components
import {
  QuoteStats,
  ContactStats,
  QuoteFilters,
  ContactFilters,
  QuotesList,
  ContactsList,
  QuoteDetailsModal,
  ContactDetailsModal,
  DeleteConfirmDialog
} from '@/components/admin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('quotes');
  
  // Data state
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modeFilter, setModeFilter] = useState('all');
  const [contactStatusFilter, setContactStatusFilter] = useState('all');
  
  // Modal state
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showContactDetails, setShowContactDetails] = useState(false);
  
  // Stats state
  const [stats, setStats] = useState({
    total: 0, pending: 0, reviewed: 0, approved: 0, rejected: 0
  });
  const [contactStats, setContactStats] = useState({
    total: 0, new: 0, read: 0, replied: 0
  });
  
  // Delete dialog state
  const [deleteQuoteId, setDeleteQuoteId] = useState(null);
  const [deleteContactId, setDeleteContactId] = useState(null);
  const [showDeleteQuoteDialog, setShowDeleteQuoteDialog] = useState(false);
  const [showDeleteContactDialog, setShowDeleteContactDialog] = useState(false);

  // ============ API FUNCTIONS ============
  
  const fetchQuotes = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await axios.get(`${API}/quotes/all`);
      const quotesData = response.data || [];
      setQuotes(quotesData);
      setStats({
        total: quotesData.length,
        pending: quotesData.filter(q => q.status === 'pending').length,
        reviewed: quotesData.filter(q => q.status === 'reviewed').length,
        approved: quotesData.filter(q => q.status === 'approved').length,
        rejected: quotesData.filter(q => q.status === 'rejected').length
      });
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        setUnauthorized(true);
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchContacts = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await axios.get(`${API}/contacts`);
      const contactsData = response.data || [];
      setContacts(contactsData);
      setContactStats({
        total: contactsData.length,
        new: contactsData.filter(c => c.status === 'new').length,
        read: contactsData.filter(c => c.status === 'read').length,
        replied: contactsData.filter(c => c.status === 'replied').length
      });
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  }, [isAuthenticated]);

  const updateQuoteStatus = async (quoteId, newStatus) => {
    try {
      await axios.patch(`${API}/quotes/${quoteId}/status?status=${newStatus}`);
      fetchQuotes();
      if (selectedQuote?.id === quoteId) {
        setSelectedQuote(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const updateContactStatus = async (contactId, newStatus) => {
    try {
      await axios.patch(`${API}/contacts/${contactId}/status?status=${newStatus}`);
      fetchContacts();
      if (selectedContact?.id === contactId) {
        setSelectedContact(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Failed to update contact status:', error);
    }
  };

  const deleteQuote = async (quoteId) => {
    try {
      await axios.delete(`${API}/quotes/${quoteId}`);
      toast.success('Quote request deleted successfully');
      fetchQuotes();
      setShowDetails(false);
      setSelectedQuote(null);
    } catch (error) {
      console.error('Failed to delete quote:', error);
      toast.error('Failed to delete quote request');
    }
  };

  const deleteContact = async (contactId) => {
    try {
      await axios.delete(`${API}/contacts/${contactId}`);
      toast.success('Contact message deleted successfully');
      fetchContacts();
      setShowContactDetails(false);
      setSelectedContact(null);
    } catch (error) {
      console.error('Failed to delete contact:', error);
      toast.error('Failed to delete contact message');
    }
  };

  const downloadQuoteAsPDF = (quote) => {
    try {
      const downloadUrl = `${API}/quotes/${quote.id}/download`;
      window.open(downloadUrl, '_blank');
      toast.success(`Downloading Quote #${quote.quote_number}...`);
    } catch (error) {
      console.error('Download Error:', error);
      toast.error('Download failed');
    }
  };

  const downloadContactAsPDF = (contact) => {
    try {
      const downloadUrl = `${API}/contacts/${contact.id}/download`;
      window.open(downloadUrl, '_blank');
      toast.success('Downloading contact message...');
    } catch (error) {
      console.error('Download Error:', error);
      toast.error('Download failed');
    }
  };

  const exportToCSV = () => {
    const headers = ['Quote #', 'Date', 'Name', 'Email', 'Company', 'Origin', 'Destination', 'Mode', 'Cargo Type', 'Weight (kg)', 'Status'];
    const rows = filteredQuotes.map(q => [
      q.quote_number,
      new Date(q.created_at).toLocaleDateString(),
      q.contact_name,
      q.contact_email,
      q.company_name || '',
      `${q.origin_city}, ${q.origin_country}`,
      `${q.destination_city}, ${q.destination_country}`,
      q.transport_mode,
      q.cargo_type,
      q.weight_kg,
      q.status
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quotes_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // ============ FILTER FUNCTIONS ============
  
  const filterQuotes = useCallback(() => {
    let filtered = [...quotes];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(q => 
        q.contact_name?.toLowerCase().includes(term) ||
        q.contact_email?.toLowerCase().includes(term) ||
        q.company_name?.toLowerCase().includes(term) ||
        q.quote_number?.toLowerCase().includes(term) ||
        q.origin_city?.toLowerCase().includes(term) ||
        q.destination_city?.toLowerCase().includes(term)
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(q => q.status === statusFilter);
    }
    if (modeFilter !== 'all') {
      filtered = filtered.filter(q => q.transport_mode === modeFilter);
    }
    setFilteredQuotes(filtered);
  }, [quotes, searchTerm, statusFilter, modeFilter]);

  const filterContacts = useCallback(() => {
    let filtered = [...contacts];
    if (searchTerm && activeTab === 'contacts') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c => 
        c.name?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.company?.toLowerCase().includes(term) ||
        c.subject?.toLowerCase().includes(term)
      );
    }
    if (contactStatusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === contactStatusFilter);
    }
    setFilteredContacts(filtered);
  }, [contacts, searchTerm, contactStatusFilter, activeTab]);

  // ============ EFFECTS ============
  
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (user && user.role !== 'admin' && user.role !== 'employee') {
      setUnauthorized(true);
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (isAuthenticated && user && (user.role === 'admin' || user.role === 'employee')) {
      fetchQuotes();
      fetchContacts();
      const interval = setInterval(() => {
        fetchQuotes();
        fetchContacts();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, user, fetchQuotes, fetchContacts]);

  useEffect(() => { filterQuotes(); }, [filterQuotes]);
  useEffect(() => { filterContacts(); }, [filterContacts]);

  // ============ HANDLERS ============
  
  const handleQuoteClick = (quote) => {
    setSelectedQuote(quote);
    setShowDetails(true);
  };

  const handleContactClick = (contact) => {
    setSelectedContact(contact);
    setShowContactDetails(true);
  };

  const handleDeleteQuoteClick = (quoteId) => {
    setDeleteQuoteId(quoteId);
    setShowDeleteQuoteDialog(true);
  };

  const handleDeleteContactClick = (contactId) => {
    setDeleteContactId(contactId);
    setShowDeleteContactDialog(true);
  };

  // ============ RENDER ============
  
  if (unauthorized) {
    return (
      <div className="min-h-screen bg-steel-50 flex items-center justify-center">
        <div className="text-center p-8">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-navy-800 mb-2">{t('admin.accessDenied')}</h1>
          <p className="text-steel-600 mb-4">{t('admin.accessDeniedMsg')}</p>
          <p className="text-sm text-steel-500 mb-6">{t('admin.accessDeniedNote')}</p>
          <Button onClick={() => navigate('/')} className="btn-primary">
            {t('common.backHome')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-steel-50" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-navy-800 pt-24 pb-8">
        <div className="container-custom">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <LayoutDashboard className="w-8 h-8 text-gold" />
                <h1 className="font-outfit text-3xl font-bold text-white">{t('admin.title')}</h1>
              </div>
              <p className="text-steel-300">{t('admin.subtitle')}</p>
            </div>
            <div className="flex items-center gap-3">
              {user?.role === 'admin' && (
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/admin/users')}
                  className="border-steel-500 text-white hover:bg-navy-700"
                >
                  <User className="w-4 h-4 mr-2" />
                  {t('users.title')}
                </Button>
              )}
              <Button 
                variant="outline" 
                onClick={() => navigate('/admin/equipment')}
                className="border-steel-500 text-white hover:bg-navy-700"
              >
                <Box className="w-4 h-4 mr-2" />
                {t('equipment.title')}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => { fetchQuotes(); fetchContacts(); }}
                className="border-steel-500 text-white hover:bg-navy-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('common.refresh')}
              </Button>
              <Button onClick={exportToCSV} className="btn-accent">
                <Download className="w-4 h-4 mr-2" />
                {t('common.download')} CSV
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <Button 
            variant={activeTab === 'quotes' ? 'default' : 'outline'}
            onClick={() => setActiveTab('quotes')}
            className={activeTab === 'quotes' ? 'btn-accent' : ''}
            data-testid="quotes-tab"
          >
            <FileText className="w-4 h-4 mr-2" />
            Quote Requests
            {stats.pending > 0 && (
              <Badge className="ml-2 bg-amber-500 text-white">{stats.pending}</Badge>
            )}
          </Button>
          <Button 
            variant={activeTab === 'contacts' ? 'default' : 'outline'}
            onClick={() => setActiveTab('contacts')}
            className={activeTab === 'contacts' ? 'btn-accent' : ''}
            data-testid="contacts-tab"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Contact Messages
            {contactStats.new > 0 && (
              <Badge className="ml-2 bg-amber-500 text-white">{contactStats.new}</Badge>
            )}
          </Button>
        </div>

        {/* Quotes Tab Content */}
        {activeTab === 'quotes' && (
          <>
            <QuoteStats stats={stats} />
            <QuoteFilters 
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              modeFilter={modeFilter}
              setModeFilter={setModeFilter}
            />
            <QuotesList 
              quotes={filteredQuotes}
              loading={loading}
              pendingCount={stats.pending}
              onQuoteClick={handleQuoteClick}
            />
          </>
        )}

        {/* Contacts Tab Content */}
        {activeTab === 'contacts' && (
          <>
            <ContactStats stats={contactStats} />
            <ContactFilters 
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              statusFilter={contactStatusFilter}
              setStatusFilter={setContactStatusFilter}
            />
            <ContactsList 
              contacts={filteredContacts}
              loading={loading}
              newCount={contactStats.new}
              onContactClick={handleContactClick}
              onStatusUpdate={updateContactStatus}
            />
          </>
        )}
      </div>

      {/* Quote Details Modal */}
      <QuoteDetailsModal
        quote={selectedQuote}
        open={showDetails}
        onOpenChange={setShowDetails}
        onStatusUpdate={updateQuoteStatus}
        onDownload={downloadQuoteAsPDF}
        onDelete={handleDeleteQuoteClick}
      />

      {/* Contact Details Modal */}
      <ContactDetailsModal
        contact={selectedContact}
        open={showContactDetails}
        onOpenChange={setShowContactDetails}
        onStatusUpdate={updateContactStatus}
        onDownload={downloadContactAsPDF}
        onDelete={handleDeleteContactClick}
      />

      {/* Delete Quote Confirmation Dialog */}
      <DeleteConfirmDialog
        open={showDeleteQuoteDialog}
        onOpenChange={setShowDeleteQuoteDialog}
        title="Delete Quote Request?"
        description="This action cannot be undone. This will permanently delete the quote request from your database."
        onConfirm={() => {
          deleteQuote(deleteQuoteId);
          setShowDeleteQuoteDialog(false);
        }}
      />

      {/* Delete Contact Confirmation Dialog */}
      <DeleteConfirmDialog
        open={showDeleteContactDialog}
        onOpenChange={setShowDeleteContactDialog}
        title="Delete Contact Message?"
        description="This action cannot be undone. This will permanently delete the contact message from your database."
        onConfirm={() => {
          deleteContact(deleteContactId);
          setShowDeleteContactDialog(false);
        }}
      />
    </div>
  );
};

export default AdminDashboard;
